import React, { useState, useEffect } from 'react';
import { SavedAnalysis, getSavedAnalyses, getAnalysisById, deleteAnalysis, updateAnalysisTitle, clearAllAnalyses, exportAnalyses, getStorageInfo } from '../../../utils/analysisStorage';
import { useAuth } from '../../../contexts/AuthContext';

// V2 디자인 시스템
const designSystem = {
  colors: {
    primary: '#2467E7',
    primaryDark: '#1B4DB8',
    primaryLight: '#E8F2FF',
    secondary: '#6C5CE7',
    accent: '#00D4FF',
    success: '#00B894',
    warning: '#FDCB6E',
    danger: '#E17055',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textMuted: '#64748b',
    bgPrimary: '#ffffff',
    bgSecondary: '#f8fafc',
    border: '#e2e8f0'
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)'
  },
  typography: {
    h2: { fontSize: '1.875rem', fontWeight: '700', lineHeight: '1.2' },
    h3: { fontSize: '1.125rem', fontWeight: '600', lineHeight: '1.3' },
    body: { fontSize: '1rem', fontWeight: '400', lineHeight: '1.5' },
    caption: { fontSize: '0.875rem', fontWeight: '500', lineHeight: '1.4' }
  }
};

interface SavedAnalysesModalV2Props {
  isOpen: boolean;
  onClose: () => void;
  onLoadAnalysis: (analysis: SavedAnalysis) => void;
}

const SavedAnalysesModalV2: React.FC<SavedAnalysesModalV2Props> = ({ isOpen, onClose, onLoadAnalysis }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [analyses, setAnalyses] = useState<SavedAnalysis[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [storageInfo, setStorageInfo] = useState({ count: 0, maxCount: 200 });
  const [loadingAnalysisId, setLoadingAnalysisId] = useState<string | null>(null); // 불러오기 로딩 상태

  // 페이징 상태
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // 필터링 상태
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<'today' | 'yesterday' | '3days' | '7days' | '15days' | '30days' | 'all'>('all');

  useEffect(() => {
    if (isOpen && user && !authLoading) {
      console.log('V2 저장된 분석 로드 시작 - 인증된 사용자:', user.email);
      setCurrentPage(1); // 모달 열 때마다 첫 페이지로 리셋
      setSearchQuery(''); // 검색어 초기화
      setDateFilter('all'); // 날짜 필터 초기화
      loadAnalyses();
      loadStorageInfo();
    }
  }, [isOpen, user, authLoading]);

  const loadAnalyses = async () => {
    setLoading(true);
    try {
      console.log('V2 getSavedAnalyses 호출 시작');
      const savedAnalyses = await getSavedAnalyses();
      console.log('V2 받은 분석 데이터:', savedAnalyses?.length, '개');
      setAnalyses(savedAnalyses);

      // 현재 페이지가 총 페이지 수를 초과하면 마지막 페이지로 이동
      const newTotalPages = Math.ceil(savedAnalyses.length / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    } catch (error) {
      console.error('V2 분석 목록 로드 실패:', error);
      setAnalyses([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStorageInfo = async () => {
    try {
      const info = await getStorageInfo();
      setStorageInfo(info);
    } catch (error) {
      console.error('저장소 정보 로드 실패:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('이 분석 결과를 삭제하시겠습니까?')) return;

    try {
      await deleteAnalysis(id);
      await loadAnalyses();
      await loadStorageInfo();
    } catch (error) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleEditTitle = async (id: string) => {
    if (!editTitle.trim()) return;

    try {
      await updateAnalysisTitle(id, editTitle.trim());
      setEditingId(null);
      setEditTitle('');
      await loadAnalyses();
    } catch (error) {
      alert('제목 수정 중 오류가 발생했습니다.');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('모든 저장된 분석 결과를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;

    try {
      await clearAllAnalyses();
      await loadAnalyses();
      await loadStorageInfo();
    } catch (error) {
      alert('전체 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleExport = async () => {
    try {
      await exportAnalyses();
    } catch (error) {
      alert('내보내기 중 오류가 발생했습니다.');
    }
  };

  const handleLoadAnalysis = async (analysis: SavedAnalysis) => {
    try {
      setLoadingAnalysisId(analysis.id); // 로딩 시작

      // 상세 데이터 가져오기 (경량화된 목록에는 pageSpeedData 등이 없음)
      console.log('📂 분석 상세 데이터 로드 중...', analysis.id);
      const fullAnalysis = await getAnalysisById(analysis.id);

      if (fullAnalysis) {
        console.log('✅ 분석 상세 데이터 로드 완료:', {
          id: fullAnalysis.id,
          hasPageSpeedData: !!fullAnalysis.pageSpeedData,
          hasBatchResults: !!fullAnalysis.batchResults
        });
        onLoadAnalysis(fullAnalysis);
        onClose();
      } else {
        alert('분석 데이터를 불러올 수 없습니다.');
      }
    } catch (error) {
      console.error('❌ 분석 로드 실패:', error);
      alert('분석 데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoadingAnalysisId(null); // 로딩 종료
    }
  };

  const handleShareAnalysis = (analysis: SavedAnalysis) => {
    const shareUrl = `https://speed.bc-hub.org/?v2=true&share=${analysis.id}`;

    // Clipboard API 사용 가능 여부 확인 (HTTPS 환경에서만 작동)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        alert('공유 링크가 클립보드에 복사되었습니다!');
      }).catch(err => {
        console.error('클립보드 복사 실패:', err);
        showShareUrlFallback(shareUrl);
      });
    } else {
      // HTTP 환경이나 클립보드 API 미지원 시 fallback
      showShareUrlFallback(shareUrl);
    }
  };

  const showShareUrlFallback = (url: string) => {
    // prompt로 URL 표시하여 사용자가 수동으로 복사할 수 있게 함
    const userAction = prompt(
      '클립보드 API를 사용할 수 없습니다.\n아래 링크를 복사하세요:',
      url
    );
    if (userAction !== null) {
      alert('링크가 표시되었습니다. 수동으로 복사해주세요.');
    }
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('ko-KR');
  };

  const getDomainFromUrl = (url: string) => {
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  // 필터링 로직
  const getFilteredAnalyses = () => {
    let filtered = [...analyses];

    // 검색 필터 (제목 + URL)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(analysis => {
        // 기본 검색 (제목, URL)
        if (analysis.title.toLowerCase().includes(query) || analysis.url.toLowerCase().includes(query)) {
          return true;
        }

        // batchResults 검색 (신버전/구버전 구조 모두 지원)
        if (analysis.batchResults) {
          const results = Array.isArray(analysis.batchResults)
            ? analysis.batchResults
            : (analysis.batchResults as any).results || [];
          return results.some((result: any) => result.url.toLowerCase().includes(query));
        }

        return false;
      });
    }

    // 날짜 필터
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();

      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'yesterday':
          filterDate.setDate(now.getDate() - 1);
          filterDate.setHours(0, 0, 0, 0);
          break;
        case '3days':
          filterDate.setDate(now.getDate() - 3);
          break;
        case '7days':
          filterDate.setDate(now.getDate() - 7);
          break;
        case '15days':
          filterDate.setDate(now.getDate() - 15);
          break;
        case '30days':
          filterDate.setDate(now.getDate() - 30);
          break;
      }

      filtered = filtered.filter(analysis =>
        new Date(analysis.timestamp) >= filterDate
      );
    }

    return filtered;
  };

  const filteredAnalyses = getFilteredAnalyses();

  // 페이징 계산 (필터링된 데이터 기준)
  const totalPages = Math.ceil(filteredAnalyses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAnalyses = filteredAnalyses.slice(startIndex, endIndex);

  // 페이지 변경
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 필터 변경 시 첫 페이지로 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, dateFilter]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
        backdropFilter: 'blur(5px)'
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '90vw',
          maxWidth: '1000px',
          height: '85vh',
          background: designSystem.colors.bgPrimary,
          borderRadius: '20px',
          border: `2px solid ${designSystem.colors.border}`,
          boxShadow: designSystem.shadows.xl,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div style={{
          padding: '25px 30px',
          borderBottom: `2px solid ${designSystem.colors.border}`,
          background: `linear-gradient(135deg, ${designSystem.colors.bgSecondary} 0%, ${designSystem.colors.primaryLight} 100%)`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <div>
              <h2 style={{
                ...designSystem.typography.h2,
                color: designSystem.colors.textPrimary,
                margin: '0 0 5px 0'
              }}>
                저장된 분석 결과
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                {user && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img
                      src={user.picture}
                      alt={user.name}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: `2px solid ${designSystem.colors.border}`
                      }}
                    />
                    <span style={{
                      ...designSystem.typography.caption,
                      color: designSystem.colors.textSecondary,
                      fontWeight: '600'
                    }}>
                      {user.name}
                    </span>
                  </div>
                )}
              </div>
              <p style={{
                ...designSystem.typography.caption,
                color: designSystem.colors.textMuted,
                margin: 0
              }}>
                {storageInfo.count}개 저장됨 (최대 {storageInfo.maxCount}개)
                {(searchQuery || dateFilter !== 'all') && (
                  <span style={{ marginLeft: '8px', color: designSystem.colors.secondary, fontWeight: '600' }}>
                    • 필터링: {filteredAnalyses.length}개
                  </span>
                )}
                {filteredAnalyses.length > itemsPerPage && (
                  <span style={{ marginLeft: '8px', color: designSystem.colors.primary, fontWeight: '600' }}>
                    • {startIndex + 1}-{Math.min(endIndex, filteredAnalyses.length)}개 표시 중
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                fontSize: '20px',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: designSystem.colors.bgPrimary,
                color: designSystem.colors.textMuted,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: designSystem.shadows.md,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${designSystem.colors.danger}20`;
                e.currentTarget.style.color = designSystem.colors.danger;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = designSystem.colors.bgPrimary;
                e.currentTarget.style.color = designSystem.colors.textMuted;
              }}
            >
              ✕
            </button>
          </div>

          {/* 필터 영역 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* 검색창 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="text"
                placeholder="제목 또는 URL로 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  border: `2px solid ${designSystem.colors.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: 'white'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = designSystem.colors.primary;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = designSystem.colors.border;
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    color: designSystem.colors.textMuted,
                    border: `2px solid ${designSystem.colors.border}`,
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = designSystem.colors.textMuted;
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = designSystem.colors.textMuted;
                  }}
                >
                  초기화
                </button>
              )}
            </div>

            {/* 날짜 필터 버튼들 */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { value: 'all' as const, label: '전체' },
                { value: 'today' as const, label: '오늘' },
                { value: 'yesterday' as const, label: '어제' },
                { value: '3days' as const, label: '3일' },
                { value: '7days' as const, label: '7일' },
                { value: '15days' as const, label: '15일' },
                { value: '30days' as const, label: '30일' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setDateFilter(value)}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: dateFilter === value ? designSystem.colors.primary : 'transparent',
                    color: dateFilter === value ? 'white' : designSystem.colors.textSecondary,
                    border: `2px solid ${dateFilter === value ? designSystem.colors.primary : designSystem.colors.border}`,
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (dateFilter !== value) {
                      e.currentTarget.style.backgroundColor = designSystem.colors.primaryLight;
                      e.currentTarget.style.borderColor = designSystem.colors.primary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (dateFilter !== value) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = designSystem.colors.border;
                    }
                  }}
                >
                  {label}
                </button>
              ))}
              <div style={{ flex: 1 }} />
              <button
                onClick={handleClearAll}
                disabled={analyses.length === 0}
                style={{
                  padding: '6px 14px',
                  backgroundColor: 'transparent',
                  color: designSystem.colors.danger,
                  border: `2px solid ${designSystem.colors.danger}`,
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: analyses.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: analyses.length === 0 ? 0.4 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (analyses.length > 0) {
                    e.currentTarget.style.backgroundColor = designSystem.colors.danger;
                    e.currentTarget.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (analyses.length > 0) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = designSystem.colors.danger;
                  }
                }}
              >
                전체 삭제
              </button>
            </div>
          </div>
        </div>

        {/* 콘텐츠 */}
        <div style={{
          padding: '20px 30px',
          overflow: 'auto',
          flex: 1
        }}>
          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: designSystem.colors.textMuted,
              ...designSystem.typography.body
            }}>
              로딩 중...
            </div>
          ) : analyses.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: designSystem.colors.textMuted,
              ...designSystem.typography.body
            }}>
              저장된 분석 결과가 없습니다.
            </div>
          ) : filteredAnalyses.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px'
            }}>
              <div style={{
                ...designSystem.typography.h3,
                color: designSystem.colors.textSecondary,
                marginBottom: '12px'
              }}>
                검색 결과가 없습니다
              </div>
              <div style={{
                ...designSystem.typography.body,
                color: designSystem.colors.textMuted,
                marginBottom: '20px'
              }}>
                다른 검색어나 필터를 시도해보세요.
              </div>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setDateFilter('all');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: designSystem.colors.primary,
                  border: `2px solid ${designSystem.colors.primary}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = designSystem.colors.primary;
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = designSystem.colors.primary;
                }}
              >
                필터 초기화
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gap: '20px' }}>
                {currentAnalyses.map((analysis) => {
                  // batchResults 파싱 (신버전: {results, urlLabels} vs 구버전: 배열)
                  let batchResultsArray: any = null;
                  let urlLabelsObj: any = {};

                  if (analysis.batchResults) {
                    if (Array.isArray(analysis.batchResults)) {
                      // 구버전: 배열만 있는 경우
                      batchResultsArray = analysis.batchResults;
                      urlLabelsObj = analysis.urlLabels || {};
                    } else if ((analysis.batchResults as any).results) {
                      // 신버전: {results: [...], urlLabels: {...}}
                      batchResultsArray = (analysis.batchResults as any).results;
                      urlLabelsObj = (analysis.batchResults as any).urlLabels || {};
                    }
                  }

                  return (
                <div key={analysis.id} style={{
                  padding: '24px',
                  backgroundColor: 'white',
                  border: `1px solid ${designSystem.colors.border}`,
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.borderColor = designSystem.colors.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.borderColor = designSystem.colors.border;
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {editingId === analysis.id ? (
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleEditTitle(analysis.id);
                              if (e.key === 'Escape') { setEditingId(null); setEditTitle(''); }
                            }}
                            style={{
                              flex: 1,
                              padding: '6px 10px',
                              border: `2px solid ${designSystem.colors.primary}`,
                              borderRadius: '6px',
                              fontSize: '14px',
                              outline: 'none'
                            }}
                            autoFocus
                          />
                          <button
                            onClick={() => handleEditTitle(analysis.id)}
                            style={{
                              padding: '6px 14px',
                              backgroundColor: 'transparent',
                              color: designSystem.colors.success,
                              border: `2px solid ${designSystem.colors.success}`,
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = designSystem.colors.success;
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = designSystem.colors.success;
                            }}
                          >
                            저장
                          </button>
                          <button
                            onClick={() => { setEditingId(null); setEditTitle(''); }}
                            style={{
                              padding: '6px 14px',
                              backgroundColor: 'transparent',
                              color: designSystem.colors.textMuted,
                              border: `2px solid ${designSystem.colors.textMuted}`,
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = designSystem.colors.textMuted;
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = designSystem.colors.textMuted;
                            }}
                          >
                            취소
                          </button>
                        </div>
                      ) : (
                        <h3 style={{
                          ...designSystem.typography.h3,
                          color: designSystem.colors.textPrimary,
                          margin: '0 0 8px 0',
                          cursor: 'pointer',
                          wordBreak: 'break-word'
                        }}
                        onClick={() => {
                          setEditingId(analysis.id);
                          setEditTitle(analysis.title);
                        }}>
                          {analysis.title}
                        </h3>
                      )}

                      <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '13px',
                          color: designSystem.colors.primary,
                          backgroundColor: `${designSystem.colors.primaryLight}`,
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontWeight: '600',
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}>
                          {getDomainFromUrl(analysis.url)}
                        </span>
                        <span style={{
                          fontSize: '12px',
                          color: designSystem.colors.textMuted,
                          fontWeight: '500'
                        }}>
                          {formatDate(analysis.timestamp)}
                        </span>
                      </div>

                      {/* URL 표시 - 다중 분석이면 모든 URL 표시 */}
                      <div style={{
                        ...designSystem.typography.caption,
                        color: designSystem.colors.textSecondary,
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        backgroundColor: designSystem.colors.bgSecondary,
                        padding: '12px',
                        borderRadius: '8px',
                        wordBreak: 'break-all',
                        marginBottom: '12px',
                        border: `1px solid ${designSystem.colors.border}`
                      }}>
                        {batchResultsArray && batchResultsArray.length > 0 ? (
                          // 다중 분석: 모든 URL 표시 (라벨 있으면 라벨 우선)
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {batchResultsArray.map((result: any, idx: number) => {
                              // URL에서 hostname 추출
                              const getHostname = (url: string) => {
                                try {
                                  return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace('www.', '');
                                } catch {
                                  return url;
                                }
                              };

                              const hostname = getHostname(result.url);
                              const label = urlLabelsObj?.[result.url];

                              // 표시 텍스트: 라벨 있으면 "hostname - 라벨", 없으면 "hostname"
                              const displayText = label ? `${hostname} - ${label}` : hostname;

                              return (
                                <div key={idx} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  padding: '6px',
                                  backgroundColor: result.failed ? 'rgba(225, 112, 85, 0.05)' : 'transparent',
                                  borderRadius: '6px'
                                }}>
                                  <span style={{
                                    fontSize: '11px',
                                    padding: '3px 8px',
                                    backgroundColor: result.failed ? designSystem.colors.danger : designSystem.colors.primary,
                                    color: 'white',
                                    borderRadius: '12px',
                                    fontWeight: '700',
                                    flexShrink: 0,
                                    minWidth: '24px',
                                    textAlign: 'center'
                                  }}>
                                    {idx + 1}
                                  </span>
                                  <span style={{
                                    flex: 1,
                                    textDecoration: result.failed ? 'line-through' : 'none',
                                    opacity: result.failed ? 0.5 : 1,
                                    fontSize: '13px'
                                  }}>
                                    {displayText}
                                  </span>
                                  {result.failed && (
                                    <span style={{
                                      fontSize: '11px',
                                      color: designSystem.colors.danger,
                                      backgroundColor: `${designSystem.colors.danger}20`,
                                      padding: '2px 8px',
                                      borderRadius: '10px',
                                      fontWeight: '600'
                                    }}>
                                      실패
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          // 단일 분석: 기존대로 하나만 표시
                          <div style={{ fontSize: '13px' }}>{analysis.url}</div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '8px', fontSize: '11px', flexWrap: 'wrap' }}>
                        {analysis.pageSpeedData && (
                          <span style={{
                            backgroundColor: `${designSystem.colors.primary}15`,
                            color: designSystem.colors.primary,
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontWeight: '600',
                            border: `1px solid ${designSystem.colors.primary}30`,
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}>
                            PageSpeed
                          </span>
                        )}
                        {analysis.llmAnalysis && (
                          <span style={{
                            backgroundColor: `${designSystem.colors.warning}15`,
                            color: '#D97706',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontWeight: '600',
                            border: '1px solid rgba(217, 119, 6, 0.3)',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}>
                            LLM 분석
                          </span>
                        )}
                        {batchResultsArray && batchResultsArray.length > 1 && (
                          <span style={{
                            backgroundColor: `${designSystem.colors.secondary}15`,
                            color: designSystem.colors.secondary,
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontWeight: '600',
                            border: `1px solid ${designSystem.colors.secondary}30`,
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}>
                            다중 분석 ({batchResultsArray.length}개)
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginLeft: '15px', flexShrink: 0 }}>
                      <button
                        onClick={() => handleShareAnalysis(analysis)}
                        style={{
                          padding: '8px 14px',
                          backgroundColor: 'transparent',
                          color: designSystem.colors.secondary,
                          border: `2px solid ${designSystem.colors.secondary}`,
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = designSystem.colors.secondary;
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = designSystem.colors.secondary;
                        }}
                      >
                        공유하기
                      </button>
                      <button
                        onClick={() => handleLoadAnalysis(analysis)}
                        disabled={loadingAnalysisId === analysis.id}
                        style={{
                          padding: '8px 14px',
                          backgroundColor: loadingAnalysisId === analysis.id ? designSystem.colors.primary : 'transparent',
                          color: loadingAnalysisId === analysis.id ? 'white' : designSystem.colors.primary,
                          border: `2px solid ${designSystem.colors.primary}`,
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: loadingAnalysisId === analysis.id ? 'wait' : 'pointer',
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap',
                          opacity: loadingAnalysisId === analysis.id ? 0.8 : 1,
                          minWidth: '90px'
                        }}
                        onMouseEnter={(e) => {
                          if (loadingAnalysisId !== analysis.id) {
                            e.currentTarget.style.backgroundColor = designSystem.colors.primary;
                            e.currentTarget.style.color = 'white';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (loadingAnalysisId !== analysis.id) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = designSystem.colors.primary;
                          }
                        }}
                      >
                        {loadingAnalysisId === analysis.id ? '불러오는 중...' : '불러오기'}
                      </button>
                      <button
                        onClick={() => handleDelete(analysis.id)}
                        style={{
                          padding: '8px 14px',
                          backgroundColor: 'transparent',
                          color: designSystem.colors.danger,
                          border: `2px solid ${designSystem.colors.danger}`,
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = designSystem.colors.danger;
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = designSystem.colors.danger;
                        }}
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                </div>
              ); })}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                marginTop: '24px',
                paddingTop: '24px',
                borderTop: `1px solid ${designSystem.colors.border}`
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: 'transparent',
                    color: currentPage === 1 ? designSystem.colors.textMuted : designSystem.colors.primary,
                    border: `2px solid ${currentPage === 1 ? designSystem.colors.border : designSystem.colors.primary}`,
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: currentPage === 1 ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.backgroundColor = designSystem.colors.primary;
                      e.currentTarget.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = designSystem.colors.primary;
                    }
                  }}
                >
                  이전
                </button>

                {/* 페이지 번호들 */}
                <div style={{ display: 'flex', gap: '4px' }}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // 현재 페이지 기준으로 앞뒤 2개씩만 표시
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 2 && page <= currentPage + 2)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: page === currentPage ? designSystem.colors.primary : 'transparent',
                            color: page === currentPage ? 'white' : designSystem.colors.textSecondary,
                            border: `2px solid ${page === currentPage ? designSystem.colors.primary : designSystem.colors.border}`,
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: page === currentPage ? '700' : '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            minWidth: '40px'
                          }}
                          onMouseEnter={(e) => {
                            if (page !== currentPage) {
                              e.currentTarget.style.backgroundColor = designSystem.colors.primaryLight;
                              e.currentTarget.style.borderColor = designSystem.colors.primary;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (page !== currentPage) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.borderColor = designSystem.colors.border;
                            }
                          }}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 3 || page === currentPage + 3) {
                      return (
                        <span key={page} style={{
                          padding: '8px 4px',
                          color: designSystem.colors.textMuted,
                          fontSize: '13px'
                        }}>
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: 'transparent',
                    color: currentPage === totalPages ? designSystem.colors.textMuted : designSystem.colors.primary,
                    border: `2px solid ${currentPage === totalPages ? designSystem.colors.border : designSystem.colors.primary}`,
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: currentPage === totalPages ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== totalPages) {
                      e.currentTarget.style.backgroundColor = designSystem.colors.primary;
                      e.currentTarget.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== totalPages) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = designSystem.colors.primary;
                    }
                  }}
                >
                  다음
                </button>
              </div>
            )}
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedAnalysesModalV2;
